import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify'
import classNames from 'classnames';

import { FormatToolbar, formatShortcut } from './Toolbar';
import { Collapse } from './Collapse';
import { useFocusWithin } from './useFocusWithin';
import {
    FONT_SIZE_MARKER, clampFontSize, editorColor, fromEditorColors, isWordChar, largerFontSize, normalizeFontSizes, realColor,
    smallerFontSize, toEditorColors, toHexColor,
} from '../../../functions/richText';

import './styles.scss';

// The caret's place as a count of characters from the start of root, which survives the DOM changes formatting makes.
const textOffset = (root, node, offset) => {
    const range = document.createRange();
    range.selectNodeContents(root);
    range.setEnd(node, offset);
    return range.toString().length;
};

// The element holding the first text at a range's start, whose style the toolbar shows. A range that starts
// between elements, as after selecting all, starts at the first text after that point.
const elementAtStart = (root, range) => {
    let node = range.startContainer;
    if (node.nodeType === Node.ELEMENT_NODE) {
        const after = node.childNodes[range.startOffset];
        if (after) {
            const text = after.nodeType === Node.TEXT_NODE ? after : document.createTreeWalker(after, NodeFilter.SHOW_TEXT).nextNode();
            node = text ?? after;
        }
    }
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return root.contains(element) ? element : root;
};

const placeCaret = (root, offset) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let left = offset;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (left <= node.length) {
            const selection = window.getSelection();
            selection.collapse(node, left);
            return;
        }
        left -= node.length;
    }
};

// The event title's field. It keeps its text as HTML, with the toolbar as in Word below it while the text or
// the toolbar has the focus. children are the form's own controls, always shown under it. value and onChange
// carry the HTML with real colours.
function TextEditor({
    value, onChange, placeholder, maxLength, className, multilineOnCtrlEnter, defaultColor, autoSize, onAutoSize, painter, setPainter,
    children,
}) {
    const colors = useSelector(state => state.colors.colors);
    const [focused, focusHandlers] = useFocusWithin();
    const inputRef = useRef(null);
    const lastHtml = useRef(null);
    const savedRange = useRef(null);
    // The px size meant by the <font size> marker last applied; see applyFontSize.
    const pendingSize = useRef(null);
    const [length, setLength] = useState(0);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrike, setIsStrike] = useState(false);
    const [highlight, setHighlight] = useState(null);
    const [align, setAlign] = useState('left');
    const [fontSize, setFontSize] = useState(16);
    const [fontColor, setFontColor] = useState(defaultColor ?? colors[0].color);

    // What the toolbar shows for the text at element, or at the caret when it's in the field.
    const showStateAt = (element, caretInField) => {
        if (!element) return;
        const style = window.getComputedStyle(element);
        setFontSize(Math.round(parseFloat(style.fontSize)));
        if (caretInField) {
            setIsBold(document.queryCommandState('bold'));
            setIsItalic(document.queryCommandState('italic'));
            setIsUnderline(document.queryCommandState('underline'));
            setIsStrike(document.queryCommandState('strikeThrough'));
            setHighlight(toHexColor(document.queryCommandValue('hiliteColor')));
            setAlign(['center', 'right', 'full'].find((a) => document.queryCommandState(`justify${a[0].toUpperCase()}${a.slice(1)}`))?.replace('full', 'justify') ?? 'left');
            setFontColor(realColor(toHexColor(document.queryCommandValue('foreColor')) ?? toHexColor(style.color), colors));
        } else {
            setFontColor(realColor(toHexColor(style.color), colors));
        }
    };

    const updateLength = () => setLength(inputRef.current.innerText.replace(/\n$/, '').length);

    useLayoutEffect(() => {
        if (value === lastHtml.current) return;
        const input = inputRef.current;
        input.innerHTML = DOMPurify.sanitize(toEditorColors(value ?? '', colors));
        normalizeFontSizes(input);
        lastHtml.current = value;
        updateLength();
        const firstText = document.createTreeWalker(input, NodeFilter.SHOW_TEXT).nextNode();
        showStateAt(firstText ? firstText.parentElement : input, false);
        if (!firstText && defaultColor) setFontColor(defaultColor);
    }, [value]);

    // Keeps the selection while it's in the field, for restoreSelection, and shows its style in the toolbar.
    // Browsers send selectionchange late and merge several into one, possibly after the focus has left the
    // field, so the field's own key, mouse, input and blur events save it too.
    const saveSelection = () => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0 || !inputRef.current?.contains(selection.anchorNode)) return;
        savedRange.current = selection.getRangeAt(0).cloneRange();
        showStateAt(elementAtStart(inputRef.current, savedRange.current), true);
    };

    useEffect(() => {
        document.addEventListener('selectionchange', saveSelection);
        return () => document.removeEventListener('selectionchange', saveSelection);
    }, [colors]);

    const emitChange = () => {
        const input = inputRef.current;
        normalizeFontSizes(input, pendingSize.current);
        // A field emptied by deleting keeps a stray <br>; clear it so the placeholder shows again.
        if (input.textContent === '' && !input.querySelector('hr')) input.innerHTML = '';
        // Styles taken off, such as a size replaced or a highlight removed, leave empty style attributes.
        input.querySelectorAll('[style=""]').forEach((el) => el.removeAttribute('style'));
        const html = fromEditorColors(DOMPurify.sanitize(input.innerHTML), colors);
        lastHtml.current = html;
        updateLength();
        onChange(html);
    };

    // Puts the caret or selection back where it was in the field when a toolbar control, such as the emoji
    // search, took the focus.
    const restoreSelection = () => {
        const input = inputRef.current;
        const selection = window.getSelection();
        if (document.activeElement === input && selection.rangeCount > 0 && input.contains(selection.anchorNode)) return;
        input.focus();
        const range = savedRange.current;
        if (range && input.contains(range.startContainer)) {
            selection.removeAllRanges();
            selection.addRange(range);
        } else if (!input.contains(selection.anchorNode)) {
            selection.selectAllChildren(input);
            selection.collapseToEnd();
        }
    };

    // Formats as Word does: the selection, or the whole word the caret is in, or else the text typed next.
    const format = (apply) => {
        restoreSelection();
        const input = inputRef.current;
        const selection = window.getSelection();
        const { anchorNode: node, anchorOffset: offset } = selection;
        let caret = null;
        if (selection.isCollapsed && node.nodeType === Node.TEXT_NODE
            && isWordChar(node.data[offset - 1]) && isWordChar(node.data[offset])) {
            let start = offset;
            let end = offset;
            while (isWordChar(node.data[start - 1])) start--;
            while (isWordChar(node.data[end])) end++;
            caret = textOffset(input, node, offset);
            selection.setBaseAndExtent(node, start, node, end);
        }
        document.execCommand('styleWithCSS', false, false);
        apply();
        if (caret != null) placeCaret(input, caret);
        emitChange();
    };

    const exec = (command, argument) => format(() => document.execCommand(command, false, argument));

    // execCommand only knows the seven sizes of <font size>, so a marker size is applied and then given the
    // real size, now or, when it waits for the text typed next, as that text comes in.
    const applyFontSize = (size) => {
        onAutoSize?.(false);
        pendingSize.current = size;
        exec('fontSize', FONT_SIZE_MARKER);
        setFontSize(size);
    };

    // «Авто»: the title has no sizes of its own, and the calendar sizes it to fit each cell.
    const applyAutoSize = () => {
        const input = inputRef.current;
        input.querySelectorAll('font[size]').forEach((el) => el.removeAttribute('size'));
        input.querySelectorAll('[style]').forEach((el) => { el.style.fontSize = ''; });
        emitChange();
        onAutoSize(true);
    };

    const applyColor = (color) => {
        exec('foreColor', editorColor(color, colors));
        setFontColor(color);
    };

    // A highlight taken off leaves a transparent background behind, which isn't kept.
    const applyHighlight = (color) => {
        exec('hiliteColor', color ?? 'transparent');
        inputRef.current.querySelectorAll('[style]').forEach((el) => {
            if (el.style.backgroundColor && !toHexColor(el.style.backgroundColor)) el.style.backgroundColor = '';
        });
        emitChange();
        setHighlight(color);
    };

    // Takes character formatting off, leaving the field's default colour.
    const clearFormat = () => format(() => {
        document.execCommand('removeFormat');
        if (defaultColor) document.execCommand('foreColor', false, defaultColor);
    });

    // Gives the selection, or the word the caret is in, the formatting the format painter copied.
    const paint = (f) => format(() => {
        document.execCommand('removeFormat');
        ['bold', 'italic', 'underline'].forEach((command) => f[command] && document.execCommand(command));
        if (f.strike) document.execCommand('strikeThrough');
        if (f.color) document.execCommand('foreColor', false, editorColor(f.color, colors));
        pendingSize.current = f.fontSize;
        document.execCommand('fontSize', false, FONT_SIZE_MARKER);
        if (f.highlight) document.execCommand('hiliteColor', false, f.highlight);
    });

    const applyAlign = (value) => {
        exec({ left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' }[value]);
        setAlign(value);
    };

    const shortcuts = {
        alignLeft: () => applyAlign('left'),
        alignCenter: () => applyAlign('center'),
        alignRight: () => applyAlign('right'),
        grow: () => applyFontSize(largerFontSize(fontSize)),
        shrink: () => applyFontSize(smallerFontSize(fontSize)),
        growByOne: () => applyFontSize(clampFontSize(fontSize + 1)),
        shrinkByOne: () => applyFontSize(clampFontSize(fontSize - 1)),
        clear: clearFormat,
        strike: () => exec('strikeThrough'),
    };

    const onMouseUp = () => {
        saveSelection();
        if (!painter) return;
        paint(painter.format);
        if (!painter.sticky) setPainter(null);
    };

    const insertText = (text) => {
        restoreSelection();
        const room = maxLength ? maxLength - length + window.getSelection().toString().length : Infinity;
        if (text.length > room) return;
        document.execCommand('insertText', false, text);
        emitChange();
    };

    const onKeyDown = (e) => {
        const shortcut = formatShortcut(e);
        if (shortcut) {
            e.preventDefault();
            shortcuts[shortcut]();
            return;
        }
        const typesChar = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
        if (maxLength && typesChar && length >= maxLength && window.getSelection().isCollapsed) {
            e.preventDefault();
        }
        // New text starts in the field's default colour rather than inheriting whatever the calendar draws.
        if (defaultColor && typesChar && inputRef.current.textContent === '') {
            document.execCommand('styleWithCSS', false, false);
            document.execCommand('foreColor', false, defaultColor);
        }
        if (multilineOnCtrlEnter && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            document.execCommand('insertLineBreak', false, '');
        }
    };

    const onPaste = (e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text/plain');
        const room = maxLength ? maxLength - length + window.getSelection().toString().length : Infinity;
        document.execCommand('insertText', false, text.slice(0, Math.max(room, 0)));
    };

    return (
        <div className={classNames('text-editor', className, { 'text-editor_painting': painter })}>
            <div className="text-editor__field" {...focusHandlers}>
                <div className="text-editor__input"
                    contentEditable
                    suppressContentEditableWarning
                    onKeyDown={onKeyDown}
                    onKeyUp={saveSelection}
                    onMouseUp={onMouseUp}
                    onBlur={saveSelection}
                    onInput={emitChange}
                    onPaste={onPaste}
                    placeholder={placeholder}
                    ref={inputRef}
                />
                {maxLength && length !== 0 && <div className="length-counter">
                    <span>{length}</span>
                    /
                    <span>{maxLength}</span>
                </div>}
                <Collapse open={focused}>
                    <FormatToolbar
                        state={{ bold: isBold, italic: isItalic, underline: isUnderline, strike: isStrike, fontSize, autoSize, color: fontColor, highlight, align }}
                        onToggle={(name) => exec(name === 'strike' ? 'strikeThrough' : name)}
                        onHighlight={applyHighlight}
                        onClear={clearFormat}
                        onAlign={applyAlign}
                        painter={painter}
                        setPainter={setPainter}
                        onFontSize={applyFontSize}
                        onAutoSize={onAutoSize && applyAutoSize}
                        onColor={applyColor}
                        onEmoji={insertText}
                    />
                </Collapse>
            </div>
            {children && <div className="text-editor__row text-editor__own">{children}</div>}
        </div>
    );
}


export { TextEditor };
